/** @jsxRuntime classic */
/** @jsx jsx */
import {
    DefaultButton, ChoiceGroup, PivotItem, Pivot, PrimaryButton, Spinner, MessageBar,
    MessageBarType, Toggle, Checkbox, Dialog, DialogFooter, DialogType, Label
} from '@fluentui/react';
import * as Yup from 'yup';
import useAxios from 'axios-hooks';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from "react";
import TextField from "../../../Forms/TextField";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { jsx, css } from "@emotion/react";
import PropTypes from 'prop-types';
import LogoImageSettings from './LogoImageSettings';
import EmailImages from './EmailImages';
import Colors from './Colors';
import Toast from '../../../Forms/Toast';
import FeatureSummary from '../../../Util/FeatureSummary';
import NoteComponent from '../../../Util/NoteComponent';

const container = css`
        .panel {
        margin-bottom: 20px;
        background-color: #fff;
        border: 1px solid transparent;
        border-radius: 4px;
        -webkit-box-shadow: 0 1px 1px rgba(0,0,0,.05);
        box-shadow: 0 1px 1px rgba(0,0,0,.05);
    }

    .panel-default {
        border-color: #ddd;
    }

    .panel-default > .panel-heading {
        color: #333;
        background-color: #f5f5f5;
        border-color: #ddd;
    }

    .panel-heading {
        padding: 10px 15px;
        border-bottom: 1px solid transparent;
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
    }

    .panel-body {
        padding: 15px;
    }

    .form-group {
        margin-top: 5px;
        margin-bottom: 5px;
    }
    .panel-heading-em,.panel-h {
        font-size: 1.2em;
        font-weight: 700;
    }
    .ms-TextField-wrapper{
        display:flex;
    }
    .ms-Label, .ms-Checkbox-text{
        width:390px;
        text-align:right;
        font-size: 14px;
        font-weight: 600;
        color: rgb(50, 49, 48);
    }
        .ms-Checkbox-text{
        width:365px;
        margin-bottom: 10px;
    }
    .ms-TextField-fieldGroup{
        width:400px;
    }
    .ms-ChoiceFieldGroup div, .ms-Dropdown-container{
        display:flex;
    }
    .ms-ChoiceField-field{
        width:70px;
    }
    .ms-Checkbox-label{
        display: flex;
        flex-direction: row-reverse;
    }
    .ms-Checkbox-label .ms-Checkbox-checkbox{
        margin-left:20px;
    }
    .ms-Checkbox-checkbox {
        border: 1px solid #939393;
        border-radius: 4px;
    }
        .form-inline{
            margin-bottom:8px;
        }
    .panel-heading-cobrand{
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #333;
        background-color: #f5f5f5;
        border-color: #ddd;
    }
    .overlay{
        background-color: #ffffff85;
        position: absolute;
        inset: 0;
    }
        .ms-TextField-errorMessage{
            margin-left: 390px;
        }
`;

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

CustomMenuList.propTypes = {
    children: PropTypes.arrayOf(PropTypes.element).isRequired,
    // react-select passes other props like getValue, setValue, etc.
    // We can be more specific if needed, but 'object' catches them for now.
    props: PropTypes.object
};

const CobrandLauncher = () => {
    const [showToast, setShowToast] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState("Saved");
    const [existingClient, setExistingClient] = useState(false);
    const [showNewClient, setShowNewClient] = useState(true);
    const [newCobrand, setNewCobrand] = useState(false);
    const [selectedKey, setSelectedKey] = React.useState("0");
    const [showError, setShowError] = React.useState(false);
    const [gaAccounts, setGaAccounts] = React.useState([]);
    const [loadingGaAccounts, setLoadingGaAccounts] = React.useState(true);
    const [gaGenerationSuccessful, setGaGenerationSuccessful] = React.useState(false);
    const [isGaIntegrationEnabled, setIsGaIntegrationEnabled] = React.useState(true);
    const [showGaConfirmModal, setShowGaConfirmModal] = React.useState(false);
    const [ga4ValidationStatus, setGa4ValidationStatus] = React.useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [ga4OverallMessage, setGa4OverallMessage] = React.useState('');
    const [ga4DetailedErrors, setGa4DetailedErrors] = React.useState([]);
    const [{ data: clients }] = useAxios('/internal/clients?bustCache=true');
    const clientsOptions = clients && clients.map(u => ({ value: u.name, label: u.name }));

    const [{ data: fetchedGaAccountsData, loading: gaAccountsLoadingState, error: gaAccountsErrorState }, fetchAccounts] = useAxios(
        { url: '/google-analytics/accounts', method: 'GET' },
        { manual: true }
    );

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    useEffect(() => {
        setLoadingGaAccounts(gaAccountsLoadingState);

        if (gaAccountsLoadingState === false) {
            if (gaAccountsErrorState) {
                console.error("Error fetching GA accounts:", gaAccountsErrorState);
                setGaAccounts([]);
                setToastMessage(`Error fetching GA accounts: ${gaAccountsErrorState.message || 'An error occurred'}`);
                setShowToast(true);
            } else if (fetchedGaAccountsData) {
                setGaAccounts(fetchedGaAccountsData.map(acc => ({ value: acc.accountId, label: acc.accountName })) || []);
            }
        }
    }, [fetchedGaAccountsData, gaAccountsLoadingState, gaAccountsErrorState, setGaAccounts, setLoadingGaAccounts, setToastMessage, setShowToast]);

    const [{ data: cobrands }] = useAxios('/internal/cobrands?bustCache=true');
    const cobrandsOptions = cobrands && cobrands.map(u => ({ value: u.cobrandId, label: u.name }));

    const [{ data: poweredByLogos }] = useAxios('/internal/cobrand/launcher/poweredByLogoTypes');
    const poweredByLogosOptions = poweredByLogos && poweredByLogos.map(u => ({ value: u.poweredByLogoTypeId, label: u.poweredByLogoType }));

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/internal/cobrand/launcher` },
        { manual: true },
    );

    const [{ loading: generatingGa }, generateGa] = useAxios(
        { url: `/google-analytics/generate`, method: 'POST' },
        { manual: true },
    );

    const [{ loading: loadingCobrandSettings }, fetchCobrandSettings] = useAxios(
        { url: '/internal/cobrand/launcher', method: 'GET' },
        { manual: true }
    );

    const [{ loading: validatingGa4, error: validateGa4ApiError }, executeValidateGa4] = useAxios(
        { url: `/google-analytics/validate-cobrand`, method: 'GET' },
        { manual: true }
    );

    const triggerGa4Validation = async (cobrandIdToValidate, currentFormValues) => {
        if (!cobrandIdToValidate) {
            setGa4ValidationStatus('idle');
            setGa4OverallMessage('');
            setGa4DetailedErrors([]);
            return;
        }

        setGa4ValidationStatus('loading');
        setGa4OverallMessage('Validating GA4 setup...');
        setGa4DetailedErrors([]);

        try {
            const response = await executeValidateGa4({ params: { cobrandId: cobrandIdToValidate } });
            if (response && response.data && Array.isArray(response.data)) {
                const validationResultsList = response.data;
                let overallValid = true;
                const currentDetailedErrors = [];

                if (validationResultsList.length === 0) {
                    setGa4ValidationStatus('error');
                    setGa4OverallMessage('No GA4 entities found for this cobrand. Consider setting up Google Analytics 4 for enhanced tracking.');
                    setGa4DetailedErrors([]);
                    return;
                }

                validationResultsList.forEach(ga4Result => {
                    const levelPrefix = ga4Result.level ? `${ga4Result.level} GA4` : "GA4";
                    if (ga4Result.steps && ga4Result.steps.length > 0) {
                        ga4Result.steps.forEach(step => {
                            if (!step.passed) {
                                overallValid = false;
                                currentDetailedErrors.push(`${levelPrefix} - ${step.errorMessage || 'Failed'}`);
                            }
                        });
                    }
                });

                if (overallValid) {
                    const levelsValidatedByApi = new Set(validationResultsList.map(item => item.level).filter(Boolean));

                    // Scenario 1: Only 'cobrandtag' was validated by API and it passed.
                    if (levelsValidatedByApi.has('CobrandTag') && levelsValidatedByApi.size === 1) {
                        setGa4ValidationStatus('warning');
                        setGa4OverallMessage('Cobrand Tag GA4 is valid.');
                        setGa4DetailedErrors([
                            'Consider setting up Client-level GA4 for comprehensive tracking.',
                            'Consider setting up Cobrand-level GA4 for comprehensive tracking.'
                        ]);
                    }
                    // Scenario 2: 'client' was validated by API and passed (but not 'cobrand').
                    else if (levelsValidatedByApi.has('Client') && !levelsValidatedByApi.has('Cobrand') && levelsValidatedByApi.size === 2) {
                        setGa4ValidationStatus('warning');
                        setGa4OverallMessage('Client GA4 is valid.');
                        setGa4DetailedErrors(['Consider setting up Cobrand-level GA4 for comprehensive tracking.']);
                    }
                    // Scenario 3: 'cobrand' was validated by API and passed (but not 'client').
                    else if (!levelsValidatedByApi.has('Client') && levelsValidatedByApi.has('Cobrand') && levelsValidatedByApi.size === 2) {
                        setGa4ValidationStatus('warning');
                        setGa4OverallMessage('Cobrand GA4 is valid.');
                        setGa4DetailedErrors(['Consider setting up Client-level GA4 for comprehensive tracking.']);
                    }
                    // Scenario 4: All three levels ('client', 'cobrand', 'cobrandtag' OR client', 'cobrand') were validated by API and passed,
                    // AND the form fields for client and cobrand are filled.
                    else if ((levelsValidatedByApi.has('Client') && levelsValidatedByApi.has('Cobrand') && levelsValidatedByApi.has('CobrandTag') || levelsValidatedByApi.has('Client') && levelsValidatedByApi.has('Cobrand')) &&
                        currentFormValues.gaClientMeasurementId?.trim() && currentFormValues.gaClientPropertyId?.trim() &&
                        currentFormValues.gaCobrandMeasurementId?.trim() && currentFormValues.gaCobrandPropertyId?.trim()) {
                        setGa4ValidationStatus('success');
                        setGa4OverallMessage('GA4 validation passed successfully. All configured levels are valid.');
                        setGa4DetailedErrors([]);
                    }
                    // Fallback Warning: API validation passed, but setup is not one of the specific success/warning scenarios above.
                    else {
                        const missingFormFieldsMessages = [];
                        if (!currentFormValues.gaClientMeasurementId?.trim() || !currentFormValues.gaClientPropertyId?.trim()) {
                            missingFormFieldsMessages.push('Client GA4 Number or Property ID is missing in the form.');
                        }
                        if (!currentFormValues.gaCobrandMeasurementId?.trim() || !currentFormValues.gaCobrandPropertyId?.trim()) {
                            missingFormFieldsMessages.push('Cobrand GA4 Number or Property ID is missing in the form.');
                        }

                        if (missingFormFieldsMessages.length > 0) {
                            setGa4ValidationStatus('warning');
                            setGa4OverallMessage('GA4 validation for configured entities passed, but essential form details are missing.');
                            setGa4DetailedErrors(missingFormFieldsMessages);
                        } else {
                            // API passed, form fields are filled, but not the ideal "all three levels validated" scenario.
                            setGa4ValidationStatus('warning');
                            setGa4OverallMessage('GA4 validation for configured entities passed.');
                            setGa4DetailedErrors(["Review GA4 setup to ensure all intended levels (Client, Cobrand, CobrandTag) are active and validated if desired."]);
                        }
                    }
                } else {
                    setGa4ValidationStatus('error');
                    setGa4OverallMessage('GA4 validation failed. See details below.');
                    setGa4DetailedErrors(currentDetailedErrors.length > 0 ? currentDetailedErrors : ['Validation failed with unspecified errors.']);
                }
            } else {
                setGa4ValidationStatus('error');
                setGa4OverallMessage('GA4 validation failed: Unexpected response from server.');
                setGa4DetailedErrors([]);
            }
        } catch (err) {
            setGa4ValidationStatus('error');
            const apiError = validateGa4ApiError || err;
            const apiErrorMsg = apiError.response?.data?.message || apiError.response?.data || apiError.message || 'An unknown error occurred.';
            setGa4OverallMessage(`GA4 validation request failed: ${apiErrorMsg}`);
            const detailedApiErrors = apiError.response?.data?.errors;
            if (Array.isArray(detailedApiErrors) && detailedApiErrors.length > 0) {
                setGa4DetailedErrors(detailedApiErrors);
            } else if (typeof apiErrorMsg === 'string' && apiErrorMsg !== 'An unknown error occurred.') {
                setGa4DetailedErrors([apiErrorMsg]);
            } else {
                setGa4DetailedErrors(['An error occurred while communicating with the server.']);
            }
            console.error("Error during GA4 validation:", apiError);
        }
    };

    const getValidationSchema = (existingClient, clients, cobrands, selectedKey, isGaIntegrationEnabled) => {
        const clientNames = (clients || []).map(c => c.name.toLowerCase());
        const cobrandNames = (cobrands || []).map(c => c.name.toLowerCase());

        const isNewClient = !existingClient && selectedKey !== "1";

        return Yup.object().shape({
            platformID: Yup.number().required('Platform is required'),

            cobrandType: Yup.string().required('Cobrand type is required'),

            clientName: Yup.string()
                .when([], {
                    is: () => isNewClient,
                    then: Yup.string()
                        .required('Client name is required')
                        .matches(/^\S+$/, 'Client name cannot contain spaces')
                        .test('unique-client', 'Client name already exists', value => {
                            if (!value) return true;
                            return !clientNames.includes(value.toLowerCase());
                        }),
                    otherwise: Yup.string()
                        .required('Client name is required')
                        .matches(/^\S+$/, 'Client name cannot contain spaces'),
                }),

            clientDescription: Yup.string()
                .nullable()
                .when([], {
                    is: () => isNewClient,
                    then: Yup.string().nullable().required('Client description is required'),
                    otherwise: Yup.string().nullable()
                }),

            ga4Account: Yup.string().when([], {
                is: () => isGaIntegrationEnabled && selectedKey === "0",
                then: Yup.string().required('GA4 Account is required when GA integration is enabled.'),
                otherwise: Yup.string().nullable(),
            }),

            gaClientMeasurementId: Yup.string()
                .nullable()
                .when([], {
                    is: () => isNewClient && !isGaIntegrationEnabled,
                    then: Yup.string().nullable().required('Client GA4 number is required when GA integration is enabled for a new client.'),
                    otherwise: Yup.string().nullable()
                }),

            gaClientPropertyId: Yup.string()
                .nullable()
                .when([], {
                    is: () => isNewClient && !isGaIntegrationEnabled,
                    then: Yup.string().nullable().required('Client property ID is required when GA integration is enabled for a new client.'),
                    otherwise: Yup.string().nullable()
                }),

            cobrandName: Yup.string()
                .required('Cobrand name is required')
                .matches(/^\S+$/, 'Cobrand name cannot contain spaces')
                .test('unique-cobrand', 'Cobrand name already exists', value => {
                    if (!value) return true;
                    if (selectedKey !== "0") return true;
                    return !cobrandNames.includes(value.toLowerCase());
                }),

            cobrandDescription: Yup.string().required('Cobrand description is required'),

            gaCobrandMeasurementId: Yup.string()
                .nullable()
                .when([], {
                    is: () => !isGaIntegrationEnabled,
                    then: Yup.string().nullable().required('Cobrand measurement id is required when GA integration is enabled.'),
                    otherwise: Yup.string().nullable()
                }),

            gaCobrandPropertyId: Yup.string()
                .nullable()
                .when([], {
                    is: () => !isGaIntegrationEnabled,
                    then: Yup.string().nullable().required('Cobrand property ID is required when GA integration is enabled.'),
                    otherwise: Yup.string().nullable()
                }),

            // Already added:
            poweredByLogoType: Yup.string().required('Powered By Logo Type is required'),
            retinaPoweredByLogoForPreNavColor: Yup.string().required('Retina Powered By Logo For Pre Nav Color is required'),
            standardPoweredByLogoForPreNavColor: Yup.string().required('Standard Powered By Logo For Pre Nav Color is required'),
            retinaPoweredByLogoForPreNavWhite: Yup.string().required('Retina Powered By Logo For Pre Nav White is required'),
            standardPoweredByLogoForPreNavWhite: Yup.string().required('Standard Powered By Logo For Pre Nav White is required'),
            pxWidthStandardLogoDesktopTabletPB: Yup.number()
                .typeError('Must be a number')
                .min(0, 'Minimum is 0')
                .max(333, 'Maximum is 333')
                .required('Desktop/Tablet logo width PB is required'),
            pxWidthStandardMobileLogoMobilePB: Yup.number()
                .typeError('Must be a number')
                .min(0, 'Minimum is 0')
                .max(287, 'Maximum is 287')
                .required('Mobile logo width PB is required'),

            // New additions below:
            retinaLogoColor: Yup.string().required('Retina Logo Color is required'),
            standardLogoColor: Yup.string().required('Standard Logo Color is required'),
            retinaLogoWhite: Yup.string().required('Retina Logo White is required'),
            standardLogoWhite: Yup.string().required('Standard Logo White is required'),
            retinaLogoForNav: Yup.string().required('Retina Logo For Nav is required'),
            standardLogoForNav: Yup.string().required('Standard Logo For Nav is required'),
            pxWidthStandardLogoDesktopTablet: Yup.number()
                .typeError('Must be a number')
                .min(1, 'Minimum is 1')
                .max(320, 'Maximum is 320')
                .required('Standard Logo Desktop/Tablet width is required'),
            retinaMobileLogoForNav: Yup.string().required('Retina Mobile Logo For Nav is required'),
            standardMobileLogoForNav: Yup.string().required('Standard Mobile Logo For Nav is required'),
            retinaMobileAltLogoColor: Yup.string().required('Retina Mobile Alt Logo Color is required'),
            standardMobileAltLogoColor: Yup.string().required('Standard Mobile Alt Logo Color is required'),
            retinaMobileAltLogoWhite: Yup.string().required('Retina Mobile Alt Logo White is required'),
            standardMobileAltLogoWhite: Yup.string().required('Standard Mobile Alt Logo White is required'),
            pxWidthStandardMobileLogoMobile: Yup.number()
                .typeError('Must be a number')
                .min(1, 'Minimum is 1')
                .max(198, 'Maximum is 198')
                .required('Standard Mobile Logo width is required'),
            emlHeaderWaveURL: Yup.string().required('Header Wave URL is required'),
            emlFooterWaveURL: Yup.string().required('Footer Wave URL is required'),
        });
    };


    const handleCobrandChange = async (option, setFieldValue, formikValues) => {
        setFieldValue('cobrandID', option.value);
        const selectedCobrandId = option.value;

        const result = await fetchCobrandSettings({
            url: `/internal/cobrand/launcher/${selectedCobrandId}`,
        });

        if (!result.error) {
            const settings = result.data;
            Object.keys(settings).forEach((key) => {
                setFieldValue(key, settings[key]);
            });
            if (selectedKey === "1") {
                const updatedFormValues = { ...formikValues, ...settings };
                triggerGa4Validation(selectedCobrandId, updatedFormValues);
            }
        } else {
            setShowError(true);
        }
    };

    const handleLinkClick = (item, resetForm, formikValues) => {
        const newSelectedKey = item.props.itemKey;

        setGa4ValidationStatus('idle');
        setGa4OverallMessage('');
        setGa4DetailedErrors([]);

        setSelectedKey(newSelectedKey);

        if (newSelectedKey === "1" && formikValues && formikValues.cobrandID) {
            triggerGa4Validation(formikValues.cobrandID, formikValues);
        }

        const emptyValues = {
            platformID: null,
            cobrandType: null,
            clientName: null,
            clientDescription: null,
            gaClientMeasurementId: null,
            gaClientPropertyId: null,
            clientId: null,
            cobrandID: null,
            cobrandName: null,
            cobrandDescription: null,
            gaCobrandMeasurementId: null,
            gaCobrandPropertyId: null,
            createCourse: null,
            poweredByLogoType: null,
            poweredByLogoTypeId: null,
            retinaPoweredByLogoForPreNavColor: null,
            standardPoweredByLogoForPreNavColor: null,
            retinaPoweredByLogoForPreNavWhite: null,
            standardPoweredByLogoForPreNavWhite: null,
            pxWidthStandardLogoDesktopTabletPB: null,
            pxWidthStandardMobileLogoMobilePB: null,
            retinaLogoColor: null,
            standardLogoColor: null,
            retinaLogoWhite: null,
            standardLogoWhite: null,
            retinaLogoForNav: null,
            standardLogoForNav: null,
            pxWidthStandardLogoDesktopTablet: null,
            retinaMobileLogoForNav: null,
            standardMobileLogoForNav: null,
            retinaMobileAltLogoColor: null,
            standardMobileAltLogoColor: null,
            retinaMobileAltLogoWhite: null,
            standardMobileAltLogoWhite: null,
            pxWidthStandardMobileLogoMobile: null,
            emlHeaderWaveURL: null,
            emlFooterWaveURL: null,
            ...Object.fromEntries(
                [
                    'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7',
                    'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7',
                    'c1', 'c2',
                ].map(key => [key, null])
            ),
        };

        resetForm({ values: emptyValues });
    };

    return (
        <Formik
            initialValues={{
                platformID: null,
                cobrandType: null,
                clientName: '',
                cobrandName: '',
                ga4Account: '',
                cobrandDescription: '',
                clientDescription: null,
                gaClientMeasurementId: null,
                gaClientPropertyId: null,
                gaCobrandMeasurementId: null,
                gaCobrandPropertyId: null,
                poweredByLogoTypeId: null,
                poweredByLogoType: '',
                retinaPoweredByLogoForPreNavColor: '',
                standardPoweredByLogoForPreNavColor: '',
                retinaPoweredByLogoForPreNavWhite: '',
                standardPoweredByLogoForPreNavWhite: '',
                pxWidthStandardLogoDesktopTabletPB: '',
                pxWidthStandardMobileLogoMobilePB: '',

                retinaLogoColor: '',
                standardLogoColor: '',
                retinaLogoWhite: '',
                standardLogoWhite: '',
                retinaLogoForNav: '',
                standardLogoForNav: '',
                pxWidthStandardLogoDesktopTablet: '',
                retinaMobileLogoForNav: '',
                standardMobileLogoForNav: '',
                retinaMobileAltLogoColor: '',
                standardMobileAltLogoColor: '',
                retinaMobileAltLogoWhite: '',
                standardMobileAltLogoWhite: '',
                pxWidthStandardMobileLogoMobile: '',
                emlHeaderWaveURL: '',
                emlFooterWaveURL: '',
            }}
            validationSchema={getValidationSchema(existingClient, clients, cobrands, selectedKey, isGaIntegrationEnabled)}
            onSubmit={async (values, { setFieldValue, resetForm }) => {
                const model = values;
                model.existingClient = true;
                setShowError(false)
                model.existingClient = selectedKey === "1" ? true : existingClient;

                let result = await submitForm({
                    url: "/internal/cobrand/launcher",
                    method: "POST",
                    data: model,
                });

                if (!result.isAxiosError) {
                    setFieldValue("cobrandID", result.data);
                    setShowNewClient(true);
                    setNewCobrand(true)
                    setSelectedKey("1");
                    setShowToast(true);
                } else {
                    setShowError(true)
                }
            }}
            validateOnBlur
            enableReinitialize={true}
            isInitialValid={false}
        >
            {({ values, setFieldValue, handleSubmit, isValid, resetForm, errors, touched }) => {
                return (
                    <Form style={{ maxWidth: "1000px", marginBottom: 500 }} css={container}>
                        {showError && <MessageBar messageBarType={MessageBarType.error}>An Error Has Occured!</MessageBar>}
                        <Pivot selectedKey={String(selectedKey)} onLinkClick={(item) => { handleLinkClick(item, resetForm, values); setNewCobrand(false); }} style={{ marginBottom: 10 }}>
                            <PivotItem headerText="Add" itemKey="0"></PivotItem>
                            <PivotItem headerText="Edit" itemKey="1"></PivotItem>
                        </Pivot>
                        {selectedKey === "1" && <div className="panel panel-default">
                            <div className="panel-heading panel-heading-cobrand"><div className='panel-h'>Select Cobrand</div>
                            </div>
                            <div className="panel-body">
                                <Select
                                    components={{ MenuList: CustomMenuList }}
                                    options={cobrandsOptions}
                                    placeholder="Select Cobrand"
                                    name="clientId"
                                    value={cobrandsOptions?.find(
                                        (option) => option.value === values.cobrandID
                                    )}
                                    onChange={(option) => {
                                        handleCobrandChange(option, setFieldValue, values);
                                    }}
                                    isSearchable
                                    styles={{
                                        menuList: (base) => ({
                                            ...base,
                                            maxHeight: "300px",
                                            overflowY: "auto",
                                        }),
                                        container: (base) => ({
                                            ...base,
                                            width: 500,
                                        }),
                                        control: (base) => ({
                                            ...base,
                                            border: "1px solid #aaaaaa",
                                        }),
                                    }}
                                />
                            </div>
                        </div>
                        }
                        {selectedKey === "1" && (ga4ValidationStatus !== 'idle' || validatingGa4) && (
                            <div style={{ margin: '15px 0', paddingLeft: '15px', paddingRight: '15px' }}>
                                {(ga4ValidationStatus === 'loading' || (validatingGa4 && ga4ValidationStatus === 'idle')) &&
                                    <Spinner label={ga4OverallMessage || "Validating GA4 setup..."} />
                                }
                                {ga4ValidationStatus === 'success' && (
                                    <MessageBar
                                        messageBarType={MessageBarType.success}
                                        isMultiline={false}
                                        onDismiss={() => {
                                            setGa4ValidationStatus('idle');
                                            setGa4OverallMessage('');
                                            setGa4DetailedErrors([]);
                                        }}
                                        dismissButtonAriaLabel="Close"
                                    >
                                        {ga4OverallMessage}
                                    </MessageBar>
                                )}
                                {ga4ValidationStatus === 'warning' && (
                                    <MessageBar
                                        messageBarType={MessageBarType.warning}
                                        isMultiline={true}
                                        onDismiss={() => {
                                            setGa4ValidationStatus('idle');
                                            setGa4OverallMessage('');
                                            setGa4DetailedErrors([]);
                                        }}
                                        dismissButtonAriaLabel="Close"
                                    >
                                        {ga4OverallMessage}
                                        {ga4DetailedErrors && ga4DetailedErrors.length > 0 && (
                                            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', textAlign: 'left' }}>
                                                {ga4DetailedErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                                            </ul>
                                        )}
                                    </MessageBar>
                                )}
                                {ga4ValidationStatus === 'error' && (
                                    <MessageBar
                                        messageBarType={MessageBarType.error}
                                        isMultiline={true}
                                        onDismiss={() => {
                                            setGa4ValidationStatus('idle');
                                            setGa4OverallMessage('');
                                            setGa4DetailedErrors([]);
                                        }}
                                        dismissButtonAriaLabel="Close"
                                    >
                                        {ga4OverallMessage}
                                        {ga4DetailedErrors && ga4DetailedErrors.length > 0 && (
                                            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', textAlign: 'left' }}>
                                                {ga4DetailedErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                                            </ul>
                                        )}
                                    </MessageBar>
                                )}
                            </div>
                        )}
                        {((selectedKey === "1" && values.cobrandID != null) || (selectedKey === "0")) && <>
                            <div className="panel panel-default">
                                <div className="panel-heading panel-heading-em">Client Information</div>
                                <div className="panel-body">

                                    {selectedKey === "0" && <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                                        <DefaultButton
                                            text="New Client"
                                            onClick={() => { setShowNewClient(true); setExistingClient(false) }}
                                            styles={{
                                                root: {
                                                    backgroundColor: showNewClient ? '#0078d4' : undefined,
                                                    color: showNewClient ? 'white' : undefined,
                                                    border: showNewClient ? '1px solid #0078d4' : undefined,
                                                },
                                            }}
                                        />
                                        <DefaultButton
                                            text="Existing Client"
                                            onClick={() => { setShowNewClient(false); setExistingClient(true) }}
                                            styles={{
                                                root: {
                                                    backgroundColor: !showNewClient ? '#0078d4' : undefined,
                                                    color: !showNewClient ? 'white' : undefined,
                                                    border: !showNewClient ? '1px solid #0078d4' : undefined,
                                                },
                                            }}
                                        />
                                    </div>}

                                    {showNewClient ? (
                                        <div className="form-inline">
                                            <div className="form-group">
                                                <TextField
                                                    label="New Client Name"
                                                    name="clientName"
                                                    value={values.clientName}
                                                    disabled={selectedKey === "1"}
                                                    onChange={(e, newVal) =>
                                                        setFieldValue('clientName', newVal || '')
                                                    }
                                                    errorMessage={touched.clientName && errors.clientName}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="form-inline">
                                            <div className="form-group">
                                                <Select
                                                    components={{ MenuList: CustomMenuList }}
                                                    options={clientsOptions}
                                                    placeholder="Use Existing Client"
                                                    name="clientName"
                                                    value={clientsOptions?.find(
                                                        (option) => option.value === values.clientName
                                                    )}
                                                    onChange={(option) => {
                                                        setFieldValue('clientName', option.value);
                                                    }}
                                                    isSearchable
                                                    styles={{
                                                        menuList: (base) => ({
                                                            ...base,
                                                            maxHeight: "300px",
                                                            overflowY: "auto",
                                                        }),
                                                        container: (base) => ({
                                                            ...base,
                                                            width: 400,
                                                        }),
                                                        control: (base) => ({
                                                            ...base,
                                                            border: "1px solid #aaaaaa",
                                                        }),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}


                                    {showNewClient && !newCobrand && (
                                        <>
                                            <div className="form-inline">
                                                <div className="form-group">
                                                    <TextField
                                                        label="Client Description"
                                                        name="clientDescription"
                                                        value={values.clientDescription}
                                                        onChange={(e, val) =>
                                                            setFieldValue('clientDescription', val || '')
                                                        }
                                                        required
                                                    />
                                                </div>
                                            </div>


                                            <div className="form-inline">
                                                <div className="form-group">
                                                    <TextField
                                                        label="Google Analytics Client Measurement Id"
                                                        name="gaClientMeasurementId"
                                                        value={values.gaClientMeasurementId || ''}
                                                        placeholder="G-XXXXXXXXXX"
                                                        disabled={isGaIntegrationEnabled}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-inline">
                                                <div className="form-group">
                                                    <TextField
                                                        label="Google Analytics Client Property Id"
                                                        name="gaClientPropertyId"
                                                        value={values.gaClientPropertyId || ''}
                                                        disabled={isGaIntegrationEnabled}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="panel panel-default">
                                <div className="panel-heading panel-heading-em">Cobrand Information</div>
                                <div className="panel-body">
                                    <fieldset>
                                        <div className="form-inline">
                                            <TextField
                                                label="Cobrand Name"
                                                disabled={selectedKey === "1"}
                                                name="cobrandName"
                                                className="bigger-input"
                                                value={values.cobrandName}
                                                onChange={(e, val) => setFieldValue('cobrandName', val || '')}
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Cobrand Description"
                                                name="cobrandDescription"
                                                className="bigger-input"
                                                value={values.cobrandDescription}
                                                onChange={(e, val) => setFieldValue('cobrandDescription', val || '')}
                                                required
                                            />
                                        </div>
                                        <div className="form-inline">
                                            <TextField
                                                label="Google Analytics CoBrand Measurement Id"
                                                name="gaCobrandMeasurementId"
                                                value={values.gaCobrandMeasurementId || ''}
                                                placeholder="G-XXXXXXXXXX"
                                                disabled={isGaIntegrationEnabled}
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Google Analytics CoBrand Property Id"
                                                name="gaCobrandPropertyId"
                                                value={values.gaCobrandPropertyId || ''}
                                                disabled={isGaIntegrationEnabled}
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <ChoiceGroup
                                                label="Platform"
                                                name='platformID'
                                                selectedKey={values.platformID}
                                                onChange={(e, option) => setFieldValue('platformID', parseInt(option.key))}
                                                options={[
                                                    { key: 1, text: 'iGrad' },
                                                    { key: 2, text: 'Enrich' },
                                                ]}
                                                required
                                            />
                                            <NoteComponent message="The Platform setting also adds the iGrad or Enrich Cobrand Tag for the selected platform." />
                                            {touched.platformID && errors.platformID && (
                                                <div style={{ color: 'red', fontSize: 12, marginTop: 4, marginLeft: 400 }}>Platform is required</div>
                                            )}
                                        </div>

                                        <div className="form-inline">
                                            <ChoiceGroup
                                                label="Cobrand Type"
                                                name='cobrandType'
                                                selectedKey={values.cobrandType}
                                                onChange={(e, option) => setFieldValue('cobrandType', option.key)}
                                                options={[
                                                    { key: 'A', text: 'A' },
                                                    { key: 'B', text: 'B' },
                                                ]}
                                                required
                                            />
                                            {touched.cobrandType && errors.cobrandType && (
                                                <div style={{ color: 'red', fontSize: 12, marginTop: 4, marginLeft: 400 }}>Cobrand Type is required</div>
                                            )}
                                        </div>

                                        <Checkbox
                                            className='mt20'
                                            label="Create Course"
                                            checked={values.createCourse}
                                            onChange={(e, checked) => setFieldValue('createCourse', !!checked)}
                                        />

                                    </fieldset>
                                </div>
                            </div>
                            <Colors values={values} setFieldValue={setFieldValue} />

                            <div className="panel panel-default">
                                <div className="panel-heading panel-heading-cobrand"><div className='panel-h'>Powered By Images/Logos</div>
                                    <div className="cobrand-select">
                                        <Select
                                            options={poweredByLogosOptions}
                                            placeholder="Select Powered By Logo Type"
                                            name="poweredByLogoSelector"
                                            isSearchable
                                            value={poweredByLogosOptions?.find(opt => opt.value === values.poweredByLogoTypeId)}
                                            onChange={(selectedOption) => {
                                                const selectedLogo = poweredByLogos.find(logo => logo.poweredByLogoTypeId === selectedOption.value);
                                                if (selectedLogo) {
                                                    setFieldValue("poweredByLogoTypeId", selectedLogo.poweredByLogoTypeId);
                                                    setFieldValue("poweredByLogoType", selectedLogo.poweredByLogoType);
                                                    setFieldValue("retinaPoweredByLogoForPreNavColor", selectedLogo.retinaPoweredByLogoForPreNavColor);
                                                    setFieldValue("standardPoweredByLogoForPreNavColor", selectedLogo.standardPoweredByLogoForPreNavColor);
                                                    setFieldValue("retinaPoweredByLogoForPreNavWhite", selectedLogo.retinaPoweredByLogoForPreNavWhite);
                                                    setFieldValue("standardPoweredByLogoForPreNavWhite", selectedLogo.standardPoweredByLogoForPreNavWhite);
                                                    setFieldValue("pxWidthStandardLogoDesktopTabletPB", selectedLogo.pxWidthStandardLogoDesktopTabletPB);
                                                    setFieldValue("pxWidthStandardMobileLogoMobilePB", selectedLogo.pxWidthStandardMobileLogoMobilePB);
                                                }
                                            }}
                                            styles={{
                                                menuList: (base) => ({
                                                    ...base,
                                                    maxHeight: "300px",
                                                    overflowY: "auto",
                                                }),
                                                container: (base) => ({
                                                    ...base,
                                                    width: 250,
                                                }),
                                                control: (base) => ({
                                                    ...base,
                                                    border: "1px solid #aaaaaa",
                                                }),
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <fieldset>
                                        <div className="form-inline">
                                            <TextField
                                                label="Powered By Logo Type"
                                                name="poweredByLogoType"
                                                className="bigger-input"
                                                value={values.poweredByLogoType}
                                                onChange={(e, val) => setFieldValue('poweredByLogoType', val || '')}
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Retina Powered By Logo For Pre Nav Grey and/or Color"
                                                name="retinaPoweredByLogoForPreNavColor"
                                                className="bigger-input"
                                                value={values.retinaPoweredByLogoForPreNavColor}
                                                onChange={(e, val) => setFieldValue('retinaPoweredByLogoForPreNavColor', val || '')}
                                                placeholder="CDN URL slug"
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Standard Powered By Logo For Pre Nav Grey and/or Color"
                                                name="standardPoweredByLogoForPreNavColor"
                                                className="bigger-input"
                                                value={values.standardPoweredByLogoForPreNavColor}
                                                onChange={(e, val) => setFieldValue('standardPoweredByLogoForPreNavColor', val || '')}
                                                placeholder="CDN URL slug"
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Retina Powered By Logo For Pre Nav White"
                                                name="retinaPoweredByLogoForPreNavWhite"
                                                className="bigger-input"
                                                value={values.retinaPoweredByLogoForPreNavWhite}
                                                onChange={(e, val) => setFieldValue('retinaPoweredByLogoForPreNavWhite', val || '')}
                                                placeholder="CDN URL slug"
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Standard Powered By Logo For Pre Nav White"
                                                name="standardPoweredByLogoForPreNavWhite"
                                                className="bigger-input"
                                                value={values.standardPoweredByLogoForPreNavWhite}
                                                onChange={(e, val) => setFieldValue('standardPoweredByLogoForPreNavWhite', val || '')}
                                                placeholder="CDN URL slug"
                                                required
                                            />
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Px Width Standard Logo Desktop Tablet PB"
                                                name="pxWidthStandardLogoDesktopTabletPB"
                                                className="bigger-input"
                                                value={values.pxWidthStandardLogoDesktopTabletPB}
                                                onChange={(e, val) => {
                                                    if (val === '' || val == null) {
                                                        setFieldValue('pxWidthStandardLogoDesktopTabletPB', '');
                                                        return;
                                                    }
                                                    const parsed = parseInt(val, 10);
                                                    if (!isNaN(parsed) && parsed <= 333 && parsed >= 0) {
                                                        setFieldValue('pxWidthStandardLogoDesktopTabletPB', parsed);
                                                    }
                                                }}
                                                type="number"
                                                min={0}
                                                max={333}
                                                placeholder="Numeric value: Max size (333px w)"
                                                required
                                            />
                                            <div style={{ fontSize: 12, fontStyle: 'italic', color: '#666', marginLeft: 400, marginTop: 4 }}>
                                                Enter the desktop/tablet Powered By logo width in pixels. Maximum is 333px.
                                            </div>
                                        </div>

                                        <div className="form-inline">
                                            <TextField
                                                label="Px Width Standard Mobile Logo Mobile PB"
                                                name="pxWidthStandardMobileLogoMobilePB"
                                                className="bigger-input"
                                                value={values.pxWidthStandardMobileLogoMobilePB}
                                                onChange={(e, val) => {
                                                    if (val === '' || val == null) {
                                                        setFieldValue('pxWidthStandardMobileLogoMobilePB', '');
                                                        return;
                                                    }
                                                    const parsed = parseInt(val, 10);
                                                    if (!isNaN(parsed) && parsed <= 287 && parsed >= 0) {
                                                        setFieldValue('pxWidthStandardMobileLogoMobilePB', parsed);
                                                    }
                                                }}
                                                type="number"
                                                min={0}
                                                max={287}
                                                placeholder="Numeric value: Max size (287px w), ~86% of desktop width"
                                                required
                                            />
                                            <div style={{ fontSize: 12, fontStyle: 'italic', color: '#666', marginLeft: 400, marginTop: 4 }}>
                                                Enter the mobile Powered By logo width in pixels. Maximum is 287px (about 86% of the desktop width).
                                            </div>
                                        </div>
                                    </fieldset>
                                </div>
                            </div>
                            <LogoImageSettings values={values} setFieldValue={setFieldValue} type="desktop" />
                            <LogoImageSettings values={values} setFieldValue={setFieldValue} type="mobile" />

                            <EmailImages values={values} setFieldValue={setFieldValue} />
                            {loadingCobrandSettings && <div className='overlay'>
                                <Spinner className="absolute-center" label="Retrieving Settings..." />
                            </div>}
                            <Toast show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
                        </>}
                        {(selectedKey === "0" || (selectedKey === "1" && values.cobrandID)) && (
                            <div className="panel panel-default">
                                <div className="panel-heading panel-heading-em">Generate Google Analytics</div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div className="panel-body">
                                        <Toggle
                                            label="Auto Generate Google Analytics"
                                            inlineLabel
                                            checked={isGaIntegrationEnabled}
                                            onChange={(ev, checked) => setIsGaIntegrationEnabled(checked)}
                                            onText="On"
                                            offText="Off"
                                            styles={{ root: { marginBottom: '20px' } }}
                                        />
                                        {isGaIntegrationEnabled && (
                                            <div className="form-inline">
                                                <div>
                                                    <div className="form-group">
                                                        <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                                                            <Label required styles={{ root: { fontSize: '14px', fontWeight: 600, color: 'rgb(50, 49, 48)' } }}>GA4 Account</Label>
                                                            <Select
                                                                components={{ MenuList: CustomMenuList }}
                                                                options={gaAccounts}
                                                                placeholder={loadingGaAccounts ? "Loading accounts..." : "Select an account"}
                                                                name="ga4Account"
                                                                value={gaAccounts?.find(
                                                                    (option) => option.value === values.ga4Account
                                                                )}
                                                                onChange={(option) => {
                                                                    setFieldValue('ga4Account', option ? option.value : null);
                                                                }}
                                                                isSearchable
                                                                isClearable
                                                                isDisabled={loadingGaAccounts}
                                                                styles={{
                                                                    menuList: (base) => ({
                                                                        ...base,
                                                                        maxHeight: "300px",
                                                                        overflowY: "auto",
                                                                    }),
                                                                    container: (base) => ({
                                                                        ...base,
                                                                        width: 400,
                                                                    }),
                                                                    control: (base, state) => ({
                                                                        ...base,
                                                                        border: touched.ga4Account && errors.ga4Account ? "1px solid rgb(164, 38, 44)" : state.isFocused ? "1px solid #0078d4" : "1px solid #aaaaaa",
                                                                        boxShadow: touched.ga4Account && errors.ga4Account ? "0 0 0 1px rgb(164, 38, 44)" : state.isFocused ? "0 0 0 1px #0078d4" : base.boxShadow,
                                                                    }),
                                                                }}
                                                            />
                                                        </div>
                                                        {touched.ga4Account && errors.ga4Account && (
                                                            <div role="alert" style={{ color: 'rgb(164, 38, 44)', fontSize: '12px', fontWeight: 'normal', paddingTop: '5px' }}>
                                                                {errors.ga4Account}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {selectedKey === "1" && (
                                                        <div className="form-group">
                                                            <TextField
                                                                label="Cobrand Id "
                                                                className="bigger-input"
                                                                placeholder="Cobrand Id"
                                                                name="cobrandId"
                                                                value={values.cobrandID}
                                                                onChange={(e, val) => setFieldValue('cobrandId', val)}
                                                                required
                                                                disabled={true}
                                                            />
                                                        </div>
                                                    )}
                                                    <p><strong>Please fill out all required fields to generate Google Analytics Data.</strong></p>
                                                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', marginTop: '20px', gap: '10px' }}>
                                                        <PrimaryButton
                                                            iconProps={{ iconName: 'Refresh' }}
                                                            text="Generate GA Data"
                                                            disabled={(
                                                                selectedKey === "0" && (!isValid || gaGenerationSuccessful || generatingGa)
                                                            ) || (
                                                                    selectedKey === "1" && (!isValid || generatingGa)
                                                                ) || !values.ga4Account}
                                                            onClick={() => setShowGaConfirmModal(true)}
                                                        />
                                                        <Dialog
                                                            hidden={!showGaConfirmModal}
                                                            onDismiss={() => setShowGaConfirmModal(false)}
                                                            dialogContentProps={{
                                                                type: DialogType.normal,
                                                                title: 'Confirm Google Analytics Asset Generation',
                                                                subText: `Generating new Google Analytics properties and web data streams for the ${existingClient ? `cobrand: ${values.cobrandDescription}` : `cobrand: ${values.cobrandDescription} and client: ${values.clientDescription}`} will overwrite any existing Google Analytics values. Are you sure you want to proceed?`
                                                            }}
                                                            modalProps={{
                                                                isBlocking: true,
                                                                styles: { main: { maxWidth: 450 } }
                                                            }}
                                                        >
                                                            <DialogFooter>
                                                                <PrimaryButton
                                                                    onClick={() => {
                                                                        setShowGaConfirmModal(false);
                                                                        const payload = {
                                                                            cobrandId: selectedKey === "1" ? parseInt(values.cobrandID) : 0,
                                                                            clientId: existingClient && values.clientId ? parseInt(values.clientId) : 0,
                                                                            isEnrich: values.platformID === 2,
                                                                            GA4Account: values.ga4Account,
                                                                            levels: existingClient ? ["Cobrand"] : ["Cobrand", "Client"],
                                                                            cobrandDisplayName: values.cobrandDescription,
                                                                            clientDisplayName: values.clientDescription
                                                                        };

                                                                        setGaGenerationSuccessful(false);
                                                                        generateGa({ data: payload })
                                                                            .then(response => {
                                                                                const responseData = response.data;
                                                                                if (Array.isArray(responseData) && responseData.length > 0) {
                                                                                    responseData.forEach(item => {
                                                                                        const propertyId = item.gaProperty?.propertyName?.propertyId;
                                                                                        const measurementId = item.gaDataStream?.webStreamData?.measurementId;
                                                                                        if (item.level === 'Client') {
                                                                                            setFieldValue('gaClientPropertyId', propertyId || '');
                                                                                            setFieldValue('gaClientMeasurementId', measurementId || '');
                                                                                        } else if (item.level === 'Cobrand') {
                                                                                            setFieldValue('gaCobrandPropertyId', propertyId || '');
                                                                                            setFieldValue('gaCobrandMeasurementId', measurementId || '');
                                                                                        }
                                                                                    });
                                                                                    setToastMessage("GA data generated successfully");
                                                                                    setShowToast(true);
                                                                                    setGaGenerationSuccessful(true);
                                                                                } else {
                                                                                    console.warn("GA data response was not in the expected format or was empty:", responseData);
                                                                                    setToastMessage("GA data response was not in the expected format or was empty.");
                                                                                    setShowToast(true);
                                                                                }
                                                                            })
                                                                            .catch(error => {
                                                                                console.error("Error generating GA data:", error);
                                                                                setToastMessage("Error generating GA data: " + (error?.response?.data?.message || error.message));
                                                                                setShowToast(true);
                                                                            });
                                                                    }}
                                                                    text="Generate"
                                                                />
                                                                <DefaultButton onClick={() => setShowGaConfirmModal(false)} text="Cancel" />
                                                            </DialogFooter>
                                                        </Dialog>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isValid && Object.keys(errors).length > 0 && (
                            <MessageBar messageBarType={MessageBarType.warning} isMultiline styles={{ root: { marginBottom: 16 } }}>
                                Form invalid. Fields with errors: {Object.keys(errors).join(', ')}
                            </MessageBar>
                        )}
                        <PrimaryButton
                            onClick={() => handleSubmit()}
                            text={selectedKey === "1" ? "Update" : "Create"}
                            disabled={(
                                selectedKey === "0" && (!isValid || (isGaIntegrationEnabled && !gaGenerationSuccessful))
                            ) || (
                                    selectedKey === "1" && !isValid
                                ) || submitting}
                        />
                        <FeatureSummary
                            label="About Cobrand Launcher"
                            summary="Create new cobrands or edit existing cobrand settings including logos, colors, email images, and integrations. Use the 'Add' tab to create a new cobrand, or the 'Edit' tab to modify an existing one."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};
export default CobrandLauncher
